import React from 'react';
import {
  FlatList,
  ListRenderItemInfo,
  TouchableOpacity,
} from 'react-native-web';
import { colors } from '@styleseat/brand';
import {
  Box,
  Inline,
  InlineProps,
} from '@styleseat/ui';
import {
  BodyText,
  IBodyTextProps,
  Separator,
  Stack,
} from '../index';
import { ChevronIcon } from '../Icons';

export type AccordionSectionHeaderProps = {
  title: string;
  icon?: React.ComponentType<any> | React.ReactElement | null;
  expand?: boolean;
};

type AccordionSectionHeaderChevronIconProps = {
  width?: number,
  height?: number,
  color?: string,
};

export type BasicAccordionSectionHeaderProps = AccordionSectionHeaderProps & {
  containerInlineProps?: InlineProps;
  titleBodyTextProps?: IBodyTextProps;
  chevronIconProps?: AccordionSectionHeaderChevronIconProps;
};

export type AccordionSectionProps = {
  title: AccordionSectionHeaderProps['title'];
  color?: string;
  icon?: AccordionSectionHeaderProps['icon'];
  index: number;
  children: React.ReactNode;
  expand?: AccordionSectionHeaderProps['expand'];
  seoFriendly?: boolean;
  onToggleSection?: (event: { title: string, index: number, value: boolean }) => void;
  onEndReached?: ((event: { distanceFromEnd: number }) => void) | null;
  keyExtractor?: (item: any, index: number) => string;
  initialNumToRender?: number;
  HeaderComponent?: React.FC<AccordionSectionHeaderProps>;
  ItemSeparatorComponent?: React.ComponentType<any> | null;
  ListFooterComponent?: React.ComponentType<any> | React.ReactElement | null;
  FooterComponent?: React.ComponentType<any> | null;
};

export const defaultContainerInlineProps: InlineProps = {
  paddingX: 8,
  paddingY: 12,
  justifyContent: 'space-between',
  alignItems: 'center',
};

export const defaultTitleBodyTextProps: IBodyTextProps = {
  weight: 'bold',
};

export const defaultChevronIconProps: AccordionSectionHeaderChevronIconProps = {
  width: 12,
  height: 12,
};

export const BasicAccordionSectionHeader: React.FC<BasicAccordionSectionHeaderProps> = ({
  title,
  icon,
  expand,
  containerInlineProps = defaultContainerInlineProps,
  titleBodyTextProps = defaultTitleBodyTextProps,
  chevronIconProps = defaultChevronIconProps,
}) => (
  <Inline {...containerInlineProps}>
    <Inline alignItems="center" space={8} testID="accordion-section-icon">
      {icon}
      <BodyText
        {...titleBodyTextProps}
        testID={`accordion-section-${title}`}
      >
        {title}
      </BodyText>
    </Inline>
    <Box testID={`chevron-${expand ? 'up' : 'down'}`}>
      <ChevronIcon {...chevronIconProps} direction={expand ? 'up' : 'down'} />
    </Box>
  </Inline>
);

const DefaultAccordionSectionHeader: React.FC<AccordionSectionHeaderProps> = props => (
  <BasicAccordionSectionHeader {...props} />
);

export const AccordionSection: React.FC<AccordionSectionProps> = ({
  title,
  icon,
  index,
  children,
  expand,
  color,
  seoFriendly,
  initialNumToRender,
  keyExtractor,
  onEndReached,
  onToggleSection,
  HeaderComponent = DefaultAccordionSectionHeader,
  ItemSeparatorComponent,
  ListFooterComponent,
  FooterComponent,
}) => {
  const handleToggle = () => {
    if (onToggleSection) {
      onToggleSection({
        title,
        index,
        value: !expand,
      });
    }
  };

  const section = {
    title,
    data: React.Children.toArray(children),
  };
  const ChildrenList = () => (
    <FlatList
      data={section.data}
      initialNumToRender={initialNumToRender}
      onEndReached={onEndReached}
      ItemSeparatorComponent={ItemSeparatorComponent}
      ListFooterComponent={ListFooterComponent}
      keyExtractor={keyExtractor}
      disableVirtualization
      renderItem={({ item: component }: ListRenderItemInfo<any>) => component}
    />
  );
  return (
    <Stack space={0}>
      <Box color={color || colors.superLight} testID="accordion-section">
        <TouchableOpacity onPress={handleToggle}>
          <HeaderComponent title={title} icon={icon} expand={expand} />
        </TouchableOpacity>
        {seoFriendly && (
          <Box testID="seo-accordion-box" style={{ height: `${expand ? '100%' : '0'}` }}>
            <ChildrenList />
          </Box>
        )}
        {!seoFriendly && expand && (
          <ChildrenList />
        )}

        {FooterComponent && (<FooterComponent section={section} sectionIndex={index} />)}
      </Box>

      <Separator
        paddingY={0}
        appearance="extrathin"
        color={colors.lightGrey}
      />
    </Stack>
  );
};
export default AccordionSection;
